/**
 * TypeScript types for project contribution tracking.
 *
 * Mirrors backend models for type safety.
 */

export enum ProjectStatus {
  ACTIVE = "Active",
  COMPLETED = "Completed",
}

export interface TeamMember {
  userId: string;
  userName: string;
  role: string;
  contribution?: string;
  assignedAt: string; // ISO date string
}

export interface Project {
  id: string;

  // Basic Info (free text for flexibility)
  client: string;
  projectName: string;
  description: string;
  product?: string;

  // Clinical Classification (Admin-managed taxonomies)
  therapeuticArea: string;
  indication?: string;

  // Project Details (free text - AI will standardize later)
  activity: string;
  subActivity?: string;
  projectStart: string; // ISO date string
  projectEnd?: string; // ISO date string

  // Status
  status: ProjectStatus;

  // Team (embedded for simplicity)
  team: TeamMember[];

  // Metadata
  createdBy: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

export interface Contribution {
  id: string;
  projectId: string;

  // Team Member (who did the work)
  teamMemberId: string;
  teamMemberName: string;
  role: string;

  // Contribution Details (free text - AI will standardize later)
  activity: string;
  subActivity?: string;
  date: string; // ISO date string
  description: string;
  notes?: string;

  // Metadata
  createdBy: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

export interface TherapeuticArea {
  code: string;
  name: string;
  description?: string;
}

export interface Indication {
  code: string;
  name: string;
  therapeuticArea?: string; // Link to therapeutic area code
  description?: string;
}

export interface ReferenceData {
  version: number;
  updatedAt: string; // ISO date string

  // Clinical Taxonomies (controlled - MedDRA/NCIt based)
  therapeuticAreas: TherapeuticArea[];
  indications: Indication[];

  // Note: Team roles are FREE TEXT (not controlled)
  // Roles are entered freely when adding team members, just like activities
}

// Request/Response Models for API

export interface CreateProjectRequest {
  client: string;
  projectName: string;
  description: string;
  product?: string;
  therapeuticArea: string;
  indication?: string;
  activity: string;
  subActivity?: string;
  projectStart: string; // ISO date string
  team?: TeamMember[];
}

export interface UpdateProjectRequest {
  client?: string;
  projectName?: string;
  description?: string;
  product?: string;
  therapeuticArea?: string;
  indication?: string;
  activity?: string;
  subActivity?: string | null;
  projectStart?: string; // ISO date string
  projectEnd?: string | null; // ISO date string (null to clear)
  status?: ProjectStatus;
}

export interface CreateContributionRequest {
  projectId: string;
  teamMemberId: string;
  teamMemberName: string;
  role: string;
  activity: string;
  subActivity?: string;
  date: string; // ISO date string
  description: string;
  notes?: string;
}

export interface UpdateContributionRequest {
  role?: string;
  activity?: string;
  subActivity?: string;
  date?: string; // ISO date string
  description?: string;
  notes?: string;
}

export interface TransferOwnershipRequest {
  newCreatorId: string;
  newCreatorName: string;
}

export interface ProjectSummary {
  projectId: string;
  projectName: string;
  client: string;
  product?: string;
  therapeuticArea: string;
  indication?: string;
  status: ProjectStatus;
  teamSize: number;
  createdAt: string; // ISO date string
  projectStart: string; // ISO date string
  projectEnd?: string; // ISO date string
}

export interface UserProjectSummary {
  projectId: string;
  projectName: string;
  client: string;
  product?: string;
  therapeuticArea: string;
  indication?: string;
  role: string;
  contribution?: string;
  status: ProjectStatus;
  assignedAt: string; // ISO date string
}

// UI-specific types

export interface ProjectWithETag {
  project: Project;
  etag: string;
}

export interface ConcurrencyError extends Error {
  name: 'ConcurrencyError';
  message: string;
  action: 'refresh' | 'retry';
}

// Filter and sort options
export interface ProjectFilters {
  status?: ProjectStatus | 'all';
  client?: string;
  therapeuticArea?: string;
  indication?: string;
  search?: string;
}

export interface ContributionFilters {
  projectId?: string;
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  role?: string;
  search?: string;
}

export interface ContributionSearchRequest {
  userIds?: string[];
  therapeuticArea?: string;
  indication?: string;
  searchText?: string;
}

export interface ContributionSearchResult {
  // Contribution details
  contributionId: string;
  date: string; // ISO date string
  activity: string;
  subActivity?: string;
  description: string;
  notes?: string;

  // Team member details
  teamMemberId: string;
  teamMemberName: string;
  roleOnProject: string;

  // Project details
  projectId: string;
  projectName: string;
  client: string;
  therapeuticArea: string;
  indication: string;
  projectStatus: ProjectStatus;
}

export type ProjectSortField = 'projectName' | 'client' | 'status' | 'createdAt' | 'projectStart';
export type ContributionSortField = 'date' | 'projectName' | 'role';
export type SortDirection = 'asc' | 'desc';
